/**
 * Data Grid Summary Row Module
 * Provides footer summary row functionality with aggregate calculations
 */

import type { IdsDataGridColumn } from './ids-data-grid-column';

export type AggregateFunction = 'sum' | 'avg' | 'count' | 'min' | 'max' | 'custom';

export type SummaryValue = number | string | null;

export type SummaryFieldValue = number | string | null | undefined;

export type CustomAggregateFunction = (values: SummaryFieldValue[]) => SummaryValue;

export interface IdsDataGridSummaryConfig {
  /** Field to aggregate */
  field: string;
  /** Aggregate function to apply */
  function: AggregateFunction;
  /** Custom aggregate function if function is 'custom' */
  customFunction?: CustomAggregateFunction;
  /** Format the aggregated value */
  label?: string;
}

export interface IdsDataGridSummaryRowData {
  isSummaryRow: true;
  aggregates: Record<string, SummaryValue>;
}

/**
 * Calculate an aggregate value for a set of rows
 * @param {SummaryFieldValue[]} values The values to aggregate
 * @param {AggregateFunction} fn The aggregate function to use
 * @param {CustomAggregateFunction} customFn Custom function if fn is 'custom'
 * @returns {SummaryValue} The aggregated value
 */
export function calculateAggregate(
  values: SummaryFieldValue[],
  fn: AggregateFunction,
  customFn?: CustomAggregateFunction
): SummaryValue {
  if (!values || values.length === 0) return null;

  const numValues = values
    .filter((v): v is number | string => v !== null && v !== undefined && v !== '')
    .map((v) => Number(v))
    .filter((v) => !Number.isNaN(v));

  switch (fn) {
    case 'sum':
      if (numValues.length === 0) return null;
      return numValues.reduce((sum, v) => sum + v, 0);
    case 'avg':
      if (numValues.length === 0) return null;
      return numValues.reduce((sum, v) => sum + v, 0) / numValues.length;
    case 'count':
      return values.filter((v) => v !== null && v !== undefined && v !== '').length;
    case 'min':
      if (numValues.length === 0) return null;
      return Math.min(...numValues);
    case 'max':
      if (numValues.length === 0) return null;
      return Math.max(...numValues);
    case 'custom':
      return customFn ? customFn(values) : null;
    default:
      return null;
  }
}

/**
 * Generate summary row data from full dataset
 * @param {Array<Record<string, SummaryFieldValue>>} data The data to summarize
 * @param {Array<IdsDataGridSummaryConfig>} summaryConfig Summary configuration
 * @returns {IdsDataGridSummaryRowData} The summary row data
 */
export function generateSummaryRow(
  data: Array<Record<string, SummaryFieldValue>>,
  summaryConfig: Array<IdsDataGridSummaryConfig>
): IdsDataGridSummaryRowData {
  const aggregates: Record<string, SummaryValue> = {};

  summaryConfig.forEach((config) => {
    const values = data.map((row) => row[config.field]);
    const result = calculateAggregate(values, config.function, config.customFunction);
    aggregates[config.field] = result;
  });

  return {
    isSummaryRow: true,
    aggregates
  };
}

/**
 * Type for a data grid cell formatter function
 */
export type SummaryCellFormatter = (
  row: Record<string, unknown>,
  col: IdsDataGridColumn,
  idx: number,
  grid: Record<string, unknown>
) => string;

/**
 * Options for formatting a summary cell's content
 */
export interface SummaryCellFormatOptions {
  /** The column field key */
  fieldKey: string;
  /** The calculated aggregate value */
  aggregateValue: SummaryValue;
  /** The column configuration */
  column: IdsDataGridColumn;
  /** Available formatters (keyed by name) */
  formatters: Record<string, SummaryCellFormatter>;
  /** The parent data grid instance (Record to avoid circular import) */
  grid: Record<string, unknown>;
  /** The escapeHTML function */
  escapeHTML: (value?: string) => string;
}

/**
 * Generate the HTML content for a summary cell given an aggregate value and column config
 * @param {SummaryCellFormatOptions} options Formatting options
 * @returns {string} The formatted HTML content for the cell
 */
export function formatSummaryCellContent(options: SummaryCellFormatOptions): string {
  const {
    fieldKey, aggregateValue, column, formatters, grid, escapeHTML
  } = options;

  if (aggregateValue === null || aggregateValue === undefined) return '';

  const { summaryFormatter, summaryFormatterOptions, formatOptions } = column;

  if (summaryFormatter && formatters) {
    const formatterName = typeof summaryFormatter === 'string'
      ? summaryFormatter : null;

    if (formatterName && formatters[formatterName]) {
      const summaryRowData: Record<string, unknown> = {
        [fieldKey]: aggregateValue
      };
      const summaryColumn: IdsDataGridColumn = {
        ...column,
        formatter: formatterName,
        formatOptions: summaryFormatterOptions || formatOptions
      };
      return formatters[formatterName](summaryRowData, summaryColumn, 0, grid);
    }

    if (typeof summaryFormatter === 'function') {
      type SummaryFn = (
        v: SummaryValue, c: IdsDataGridColumn, g: Record<string, unknown>
      ) => string;
      const fn = summaryFormatter as unknown as SummaryFn;
      return fn(aggregateValue, column, grid);
    }
  }

  return `<span class="text-ellipsis">${escapeHTML(String(aggregateValue))}</span>`;
}
